/**
 * DocumentDraftModal — Shared component
 *
 * Extracted from DocumentLibraryPage so it can be reused by
 * MyDocumentsPage (History) and the original Document Library.
 *
 * Additions over the original inline modal:
 *  - `initialDraft` prop for pre-populating from History page
 *  - Auto-restore from IndexedDB cache on open
 *  - Fire-and-forget save to IndexedDB + backend after AI generation & on edit close
 */

import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/constants';
import { sanitizeDocHtml } from '../../utils/sanitizeHtml';
import { DOC_CSS_SCOPED } from '../../utils/docCss';
import { DOC_STAGE_STYLE, DOC_PAPER_STYLE, docEditorStyle } from './draftSurfaceStyles';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { FiZap, FiShare2, FiRotateCcw } from 'react-icons/fi';
import { TbPencil, TbLock } from 'react-icons/tb';
import DraftStatusChip from '../common/DraftStatusChip';
import ConfirmDialog from '../common/ConfirmDialog';
import HoverPopover from '../common/HoverPopover';
import DraftModeChoicePopover from '../common/DraftModeChoicePopover';
import DocumentSkeleton from '../common/DocumentSkeleton';
import DraftOptionsBar from '../common/DraftOptionsBar';
import { aiAPI, companiesAPI, draftsAPI } from '../../services/api';
import { saveDraft as cacheSave, getDraft as cacheGet, deleteDraft as cacheDelete, NO_CIN_PLACEHOLDER } from '../../services/draftCacheService';
import { useUI } from '../../context/UIContext';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { reportError } from '../../utils/errorReporter';
import ShareDraftModal from './ShareDraftModal';
import { normalizeCompanyContext, htmlToPlainText, htmlToMarkdown } from '../../utils/draftUtils';
import { useDocumentExport } from '../../hooks/useDocumentExport';
import { buildDocBasename } from '../../utils/downloadFilename';
import ExportMenu from '../common/ExportMenu';
import Icon from '../common/Icon';
import { buildDocumentFallbackHtml, toDocumentHtml } from '../../utils/documentDraftFormatter';

/**
 * @param {object}  props
 * @param {object}  props.selected        { doc } — the document template object
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {object}  [props.initialDraft]  { docHtml, docSource, draftMeta } — from History page
 * @param {string}  [props.pageOrigin]    Override origin label (default: 'document-library')
 * @param {boolean} [props.canEdit=true]  Whether user can edit the document
 */
export default function DocumentDraftModal({ selected, open, onClose, initialDraft, contentLoading = false, pageOrigin = 'document-library', canEdit = true, companyCin, companyName }) {
  const { showToast } = useUI();
  const { activeCompany, companies } = useApp();
  const { user } = useAuth();
  // Share is only offered on a SAVED draft the current user owns (the backend
  // enforces owner-only + no-personal; this just hides the dead affordance).
  const [shareOpen, setShareOpen] = useState(false);
  const canShare = Boolean(
    initialDraft?._id
    && initialDraft?.visibility !== 'personal'
    && String(initialDraft?.user?._id || initialDraft?.user || '') === String(user?.id || user?._id || ''),
  );
  const [docHtml, setDocHtml] = useState('');
  const [loading, setLoading] = useState(false);
  // Initial-load gate: true from modal open until the company lookup + cache
  // restore resolve. Drives the document-shaped skeleton (not AI generation,
  // which keeps the existing preview visible/editable in place).
  const [initializing, setInitializing] = useState(false);
  const [editable, setEditable] = useState(false);
  const [draftStatus, setDraftStatus] = useState('Preparing editable statutory preview...');
  const [lastDraftMeta, setLastDraftMeta] = useState(null);
  const [showDraftChoice, setShowDraftChoice] = useState(false);
  const [showStatusChip, setShowStatusChip] = useState(true);
  const [docSource, setDocSource] = useState('preview');
  // QA-1: deterministic filing-readiness findings for the CURRENT AI draft
  // (cleared on preview/revert — templates are placeholders by design).
  const [lintFindings, setLintFindings] = useState([]);
  // Optional free-text steer for the next AI draft. Per-open, never persisted.
  const [aiInstructions, setAiInstructions] = useState('');

  // Track whether user actually edited content (for save-on-close)
  const editedRef = useRef(false);
  const contentRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  // Bumps to supersede any in-flight AI draft (doc switch / close / newer call).
  const aiReqRef = useRef(0);
  // Guards the open-effect against spurious re-runs (companies identity churn).
  const initSigRef = useRef(null);

  const doc = selected?.doc || null;

  // Sanitize before the dangerouslySetInnerHTML sink — shared policy with every
  // other draft sink (drops scripts / handlers / <img>, keeps styles + tables).
  const safeHtml = useMemo(() => sanitizeDocHtml(docHtml), [docHtml]);

  const { copy: handleCopy, downloadTxt: handleDownloadTxt, downloadHtml: handleDownloadHtml, print: handlePrint, getHtml: getLatestHtml } = useDocumentExport(contentRef, {
    title: doc?.title || 'Document Draft',
    filename: buildDocBasename({ company: companyName || activeCompany?.name, docType: doc?.title || 'Document' }),
    fallbackHtml: docHtml,
    onNotify: showToast,
  });

  /* ── persist helper (fire-and-forget) ─────────────────────────── */
  const persistDraft = (html, source, meta, activeDoc) => {
    const record = {
      documentId: activeDoc.id,
      companyCin: companyCin,
      companyName: companyName,
      documentTitle: activeDoc.title,
      documentType: activeDoc.category === 'resolution' ? 'resolution' : 'document',
      pageOrigin,
      docSource: source,
      docHtml: html,
      draftMeta: meta || {},
    };
    cacheSave(record).catch(() => { });
    draftsAPI.save(record).catch(() => { });
  };

  /** Debounced auto-save (IndexedDB + backend) for refresh recovery & History. */
  const scheduleAutoSave = () => {
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      if (!contentRef.current || !doc) return;
      persistDraft(contentRef.current.innerHTML, 'user-edited', lastDraftMeta, doc);
    }, 2000);
  };

  /* ── AI generation ────────────────────────────────────────────── */
  const runAIDraft = async (activeDoc, options = {}) => {
    const reqId = ++aiReqRef.current;
    const useCache = options.useCache !== false;
    const trigger = options.trigger || 'auto';

    setLoading(true);
    setShowStatusChip(true);
    const startedAt = Date.now();
    setDraftStatus(
      useCache
        ? 'AI is processing your draft in the background. You can keep reviewing or editing this preview.'
        : 'Generating a fresh AI draft. This can take up to a minute while preview stays editable.',
    );

    const companyContext = normalizeCompanyContext(activeCompany || {});

    // Free-text steer typed into the AI-draft strip, for this generation only.
    const instructions = aiInstructions.trim() || undefined;

    try {
      let response;

      if (activeDoc.category === 'resolution') {
        response = await aiAPI.draftResolution(
          activeDoc.title,
          // Markdown preserves table / list / heading structure across the
          // regeneration round-trip; see ResolutionModal for the rationale.
          htmlToMarkdown(getLatestHtml()) || null,
          companyContext,
          { useCache, instructions },
        );
      } else {
        response = await aiAPI.draftDocument(
          activeDoc.title,
          companyContext,
          {
            useCache,
            section: activeDoc.section,
            category: activeDoc.category,
            documentId: activeDoc.id,
            existingText: htmlToMarkdown(getLatestHtml()) || null,
            instructions,
          },
        );
      }
      // Bail if superseded / doc switched / closed during the in-flight call.
      if (reqId !== aiReqRef.current) return;

      const aiHtml = response?.data?.html || response?.html;
      const aiDraft = response?.data?.content || response?.content;
      const draftMeta = response?.data?.telemetry || response?.telemetry || {};
      const draftSource = response?.data?.source || response?.source || '';
      // QA-1 filing-readiness findings computed server-side with the draft.
      setLintFindings(response?.data?.lint?.findings || response?.lint?.findings || []);
      const isTemplateFallback = draftSource === 'fallback' || draftSource === 'ai-fallback-normalized';
      const completedAt = Date.now();

      const hasGenuineAI = !isTemplateFallback && (
        (aiHtml && `${aiHtml}`.trim()) ||
        (aiDraft && `${aiDraft}`.trim())
      );

      if (hasGenuineAI) {
        const meta = {
          ...draftMeta,
          source: draftSource,
          generatedAt: completedAt,
          latencyMs: draftMeta?.latencyMs || completedAt - startedAt,
        };
        setLastDraftMeta(meta);
        const rendered = aiHtml?.trim() || toDocumentHtml(activeDoc, `${aiDraft}`, companyContext);
        setDocHtml(rendered);
        setDocSource('ai');
        setDraftStatus(
          draftMeta?.cacheHit
            ? 'Recent draft loaded quickly.'
            : trigger === 'manual'
              ? 'Fresh AI draft generated.'
              : 'AI draft updated successfully.',
        );

        // ── persist draft (fire-and-forget) ──
        persistDraft(rendered, 'ai', meta, activeDoc);
      } else {
        setDocHtml(buildDocumentFallbackHtml(activeDoc, companyContext));
        setDocSource('fallback');
        setLintFindings([]); // templates are placeholders by design — no nagging
        setDraftStatus('Statutory preview template · AI currently unavailable.');
      }
    } catch (err) {
      if (err?.status === 401 || err?.status === 403) return;
      if (reqId !== aiReqRef.current) return;

      setDocHtml(buildDocumentFallbackHtml(doc, companyContext));
      setDocSource('fallback');
      setLintFindings([]);
      setDraftStatus('Statutory preview template · AI currently unavailable.');
    } finally {
      if (reqId === aiReqRef.current) setLoading(false);
    }
  };
  // useCallback so the identity is stable while `companies` is the same —
  // lets the open-effect below list this in its deps array honestly.
  const fetchCompanyByCin = useCallback(async (cin) => {
    // Serve from the already-loaded company list first — avoids a network
    // round-trip on every modal open. A CIN absent from the list falls
    // through to the API (the verify-on-open / 404 safety net stays intact).
    const fromContext = (companies || []).find(
      (c) => String(c.cin || '').toUpperCase() === String(cin || '').toUpperCase()
    );
    if (fromContext) return fromContext;
    try {
      const response = await companiesAPI.getByCin(cin);
      if (response.success) {
        return response.data;
      }
    } catch (error) {
      reportError({
        name: error?.name || 'CompanyLookupError',
        message: `DocumentDraftModal company lookup failed (cin=${cin}): ${error?.message || String(error)}`,
        stack: error?.stack,
      });
    }
    // Safe fallback — callers downstream read .cin / .name unconditionally.
    return { name: '', cin: '' };
  }, [companies]);
  /* ── open effect: auto-restore or initialDraft ────────────────── */
  useEffect(() => {
    // Closed → clear the per-open guard and supersede any in-flight AI draft.
    if (!open || !doc) {
      initSigRef.current = null;
      aiReqRef.current += 1;
      setInitializing(false);
      return;
    }
    // Re-init only on a real (doc, company, source) change — not on companies churn.
    // The `loading` segment keeps this keyed while the History page hydrates the
    // saved draft, so the effect re-runs once real content lands.
    const sig = `${doc.id || ''}::${companyCin}::${initialDraft ? 'draft' : 'preview'}::${contentLoading ? 'loading' : 'ready'}`;
    if (initSigRef.current === sig) return;
    initSigRef.current = sig;
    aiReqRef.current += 1;
    setLoading(false);
    setInitializing(true);

    // Opened from History before the saved draft has arrived — hold the skeleton
    // (initializing) and skip the cache/preview path until content is passed in.
    if (contentLoading) return;

    const initializeModal = async () => {
      
      let companyContext = { name: '', cin: '' };
      if (companyCin) {
        try {
          companyContext = await fetchCompanyByCin(companyCin);
        } catch (error) {
          reportError({
            name: error?.name || 'CompanyLookupError',
            message: `DocumentDraftModal fetchCompanyByCin threw: ${error?.message || String(error)}`,
            stack: error?.stack,
          });
        }
      }

    setEditable(false);
      editedRef.current = false;
      setLastDraftMeta(null);
      setShowDraftChoice(false);
      setShowStatusChip(false);
      setAiInstructions('');

      // 1. If opened from History page with initialDraft, use that
      if (initialDraft) {
        setDocHtml(initialDraft.docHtml || '');
        setDocSource(initialDraft.docSource || 'ai');
        setLastDraftMeta(initialDraft.draftMeta || null);
        setDraftStatus('Restored from saved draft.');
        setShowStatusChip(true);
        setInitializing(false);
        return;
      }

      // 2. IndexedDB auto-restore. `effectiveCin` (not `companyCin`) —
      // using the prop name as `const` here would shadow & TDZ the prop.
      const effectiveCin = companyContext.cin;
      cacheGet(doc.id, effectiveCin).then(async (cached) => {
        if (cached && cached.docHtml) {
          // Verify-on-open: if the cached CIN is no longer in the user's system,
          // evict + reset to preview. Only 404 evicts; other errors keep the body.
          if (cached.companyCin && cached.companyCin !== NO_CIN_PLACEHOLDER) {
            try {
              const resp = await companiesAPI.getByCin(cached.companyCin);
              if (!resp || resp.success === false || !resp.data?.cin) {
                throw new Error('company-not-found');
              }
            } catch (err) {
              if (err?.statusCode === 404 || err?.message === 'company-not-found') {
                cacheDelete(cached.documentId, cached.companyCin).catch(() => {});
                setDocSource('preview');
                setDocHtml(buildDocumentFallbackHtml(doc, companyContext));
                setDraftStatus('Previous draft referenced a deleted company. Reset to template.');
                setShowStatusChip(true);
                return;
              }
              // Other errors \u2014 lenient, render cached body.
            }
          }
          setDocHtml(cached.docHtml);
          setDocSource(cached.docSource || 'ai');
          setLastDraftMeta(cached.draftMeta || null);
          setDraftStatus('Restored from your previous draft.');
          setShowStatusChip(true);
        } else {
          // 3. Default: statutory preview template
          setDocSource('preview');
          setDocHtml(buildDocumentFallbackHtml(doc, companyContext));
          setDraftStatus('Preview ready. Click AI Draft to generate an AI-tailored version.');
        }
      }).catch(() => {
        setDocSource('preview');
        setDocHtml(buildDocumentFallbackHtml(doc, companyContext));
        setDraftStatus('Preview ready. Click \u2728 AI Draft to generate an AI-tailored version.');
      }).finally(() => setInitializing(false));
    };

    initializeModal();
  }, [open, doc, companyCin, initialDraft, contentLoading, fetchCompanyByCin]);

  /* ── auto-hide status chip ────────────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    if (loading) { setShowStatusChip(true); return; }

    const transientStates = [
      'Recent draft loaded quickly.',
      'Fresh AI draft generated.',
      'Draft prepared using template fallback.',
      'AI draft updated successfully.',
      'Preview loaded. Click AI Draft for generated content.',
      'Restored from your previous draft.',
      'Restored from saved draft.',
    ];

    if (!transientStates.includes(draftStatus)) return;

    const timer = setTimeout(() => setShowStatusChip(false), 5000);
    return () => clearTimeout(timer);
  }, [open, loading, draftStatus]);

  /* ── Warn on refresh/close while editing + cleanup auto-save ─── */
  useEffect(() => {
    if (!editable) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      clearTimeout(autoSaveTimerRef.current);
    };
  }, [editable]);

  /* ── Revert to the standard preview ───────────────────────────── */
  // Non-destructive by design: resets the on-screen document and clears the
  // DEVICE cache so the next open starts from the template — the AI/edited
  // version already saved to My Documents stays there untouched (persistDraft
  // writes to the backend on every generation/edit). Offered only on template
  // surfaces: a doc opened from My Documents (initialDraft) IS the saved
  // version, so "revert to template" would be meaningless there.
  const [confirmRevert, setConfirmRevert] = useState(false);
  const handleRevert = async () => {
    setConfirmRevert(false);
    if (!doc) return;
    aiReqRef.current += 1; // supersede any in-flight AI draft
    clearTimeout(autoSaveTimerRef.current); // a pending autosave must not resurrect it
    let companyContext = { name: '', cin: '' };
    if (companyCin) {
      try { companyContext = await fetchCompanyByCin(companyCin); } catch { /* preview renders placeholders */ }
    }
    cacheDelete(doc.id, companyContext.cin).catch(() => {});
    // Fallback templates were never persisted — don't promise a saved copy.
    const hadSavedVersion = docSource === 'ai' || docSource === 'user-edited';
    editedRef.current = false;
    setEditable(false);
    setLintFindings([]);
    setDocHtml(buildDocumentFallbackHtml(doc, companyContext));
    setDocSource('preview');
    setLastDraftMeta(null);
    setDraftStatus('Reverted to the standard preview.');
    setShowStatusChip(true);
    showToast(
      hadSavedVersion
        ? 'Reverted to preview — your previous version is still in My Documents'
        : 'Reverted to the standard preview',
      'success',
    );
  };

  /* ── save on close if user edited ─────────────────────────────── */
  const handleClose = () => {
    const html = getLatestHtml();
    if (editedRef.current) setDocHtml(html);
    if (editedRef.current && doc) {
      persistDraft(html, 'user-edited', lastDraftMeta, doc);
    }
    editedRef.current = false;
    onClose();
  };

  /* ── toolbar handlers ─────────────────────────────────────────── */
  const handleManualAIDraft = async () => {
    if (!doc || loading) return;
    if (lastDraftMeta) { setShowDraftChoice((s) => !s); return; }
    await runAIDraft(doc, { useCache: false, trigger: 'manual' });
  };

  const handleChooseRecentDraft = async () => {
    setShowDraftChoice(false);
    await runAIDraft(doc, { useCache: true, trigger: 'manual' });
  };

  const handleChooseFreshDraft = async () => {
    setShowDraftChoice(false);
    await runAIDraft(doc, { useCache: false, trigger: 'manual' });
  };

  /* Copy/Download/Print handlers provided by useDocumentExport hook */

  /* ── Expand into the AI chat ──────────────────────────────────── */
  // Hands the CURRENT on-screen document (including unsaved edits) to the
  // agent as an artifact, in a fresh conversation. Saved drafts keep their
  // identity (_id/refNumber) so save-back can update the original.
  const navigate = useNavigate();
  const handleContinueInChat = () => {
    const html = getLatestHtml();
    if (editedRef.current) setDocHtml(html);
    navigate(ROUTES.AGENT, {
      state: {
        openDraftDoc: {
          _navNonce: `modal-${Date.now()}`,
          _id: initialDraft?._id || null,
          refNumber: initialDraft?.refNumber || null,
          docHtml: html,
          documentTitle: doc?.title || initialDraft?.documentTitle || 'Document',
          documentType: pageOrigin === 'resolutions' ? 'resolution'
            : (pageOrigin === 'report-templates' || pageOrigin === 'report-generator') ? 'report' : 'document',
          // T7 — the catalog identity travels with the document, so a resolution
          // opened through this modal is held to its statutory class in the chat
          // too (see ResolutionModal for the full rationale). Harmless for the
          // non-resolution templates: ids outside the class table resolve to no
          // constraint at all.
          catalogId: doc?.id || null,
          docSource,
          companyCin: companyCin || activeCompany?.cin || null,
          companyName: companyName || activeCompany?.name || null,
          createdByName: initialDraft?.createdByName || null,
          createdAt: initialDraft?.createdAt || null,
          updatedAt: initialDraft?.updatedAt || null,
        },
      },
    });
  };

  if (!doc) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        <div>
          <div className="flex items-center gap-2"><Icon name={doc.icon} size={18} style={{ color: '#145f5f' }} /> {doc.title}</div>
          {doc.section && <div className="text-[11px] font-normal text-[#7a7060] mt-0.5">{doc.section}</div>}
        </div>
      }
      size="xl"
      bodyClassName="p-0"
      containerClassName="!w-[min(1120px,96vw)] max-h-[95vh]"
      headerActions={
        <>
          <HoverPopover variant="tooltip" content={editable ? 'Lock document' : 'Enable edit'}>
            <Button
              size="sm"
              iconOnly
              className={`cs-btn--header-action ${editable ? 'active' : ''}`}
              aria-label={editable ? 'Lock document' : 'Enable edit'}
              onClick={() => {
                if (editable && contentRef.current) {
                  const html = contentRef.current.innerHTML;
                  setDocHtml(html);
                  if (editedRef.current && doc) {
                    setDocSource('user-edited');
                    persistDraft(html, 'user-edited', lastDraftMeta, doc);
                  }
                }
                setEditable((s) => !s);
              }}
              disabled={!canEdit}
              leftIcon={editable ? <TbLock size={16} /> : <TbPencil size={16} />}
            />
          </HoverPopover>
          <HoverPopover variant="tooltip" content={loading ? 'AI processing…' : 'AI Draft'}>
            <Button
              size="sm"
              iconOnly
              className="cs-btn--header-action"
              aria-label="AI Draft"
              onClick={handleManualAIDraft}
              loading={loading}
              leftIcon={<FiZap size={16} />}
            />
          </HoverPopover>
          {!initialDraft && docSource !== 'preview' && !initializing && (
            <HoverPopover variant="tooltip" content="Revert to standard preview">
              <Button
                size="sm"
                iconOnly
                className="cs-btn--header-action"
                aria-label="Revert to standard preview"
                onClick={() => setConfirmRevert(true)}
                leftIcon={<FiRotateCcw size={15} />}
              />
            </HoverPopover>
          )}
          {/* One Share menu owns sharing AND every export action. The share row
              is always visible for discoverability; when sharing isn't possible
              the hint says exactly why. */}
          <ExportMenu
            size="sm"
            iconOnly
            className="cs-btn--header-action"
            title="Share & export"
            triggerIcon={<FiShare2 size={15} />}
            items={[
              {
                key: 'share',
                label: 'Share with a professional',
                icon: <FiShare2 size={15} />,
                onClick: () => setShareOpen(true),
                disabled: !canShare,
                hint: canShare
                  ? 'Notify a professional and grant read or edit access'
                  : !initialDraft?._id
                    ? 'Open a saved document from My Documents to share it'
                    : initialDraft?.visibility === 'personal'
                      ? 'Personal drafts can’t be shared'
                      : 'Only the document owner can share',
              },
              { key: 'copy',  label: 'Copy text',       onClick: handleCopy, dividerBefore: true },
              { key: 'txt',   label: 'Download .TXT',   onClick: handleDownloadTxt },
              { key: 'html',  label: 'Download .HTML',  onClick: handleDownloadHtml },
              { key: 'print', label: 'Print / PDF',     onClick: handlePrint },
            ]}
          />
        </>
      }
    >
      {/* Optional free-text steer for the AI draft (collapsed by default). */}
      <DraftOptionsBar
        instructions={aiInstructions}
        onInstructionsChange={setAiInstructions}
        disabled={loading}
      />

      <div style={DOC_STAGE_STYLE}>
        {/* Sticky overlay — stays visible while scrolling the document */}
        <div className="sticky top-0 z-30" style={{ height: 0, overflow: 'visible' }}>
          <div className="relative">
            <DraftModeChoicePopover
              show={showDraftChoice}
              onCancel={() => setShowDraftChoice(false)}
              onChooseFresh={handleChooseFreshDraft}
              onChooseRecent={handleChooseRecentDraft}
            />

            <DraftStatusChip
              show={showStatusChip}
              loading={loading}
              draftStatus={draftStatus}
              lastDraftMeta={lastDraftMeta}
            />
          </div>
        </div>

        {/* QA-1 filing-readiness strip — deterministic findings for the current AI draft. */}
        {lintFindings.length > 0 && (
          <div className="mx-auto mb-3 px-3.5 py-2.5 rounded-[10px] border flex flex-col gap-1.5" style={{ maxWidth: '820px', background: '#fff7e6', borderColor: '#eadfc0' }}>
            <div className="text-[9.5px] uppercase tracking-[0.5px] font-bold" style={{ color: '#8a6a1f' }}>Before filing</div>
            {lintFindings.slice(0, 4).map((f) => (
              <div key={f.code} className="flex items-start gap-1.5 text-[11px] leading-snug" style={{ color: f.severity === 'warning' ? '#8a6a1f' : '#7a7060' }}>
                <span className="mt-[3px] w-1.5 h-1.5 rounded-full shrink-0" style={{ background: f.severity === 'warning' ? '#b8973a' : '#a09880' }} />
                <span>{f.message}</span>
              </div>
            ))}
          </div>
        )}
        <div
          className="mx-auto bg-white relative"
          style={DOC_PAPER_STYLE}
        >
          {/* Initial-load skeleton — covers the blank page while the company
              lookup + cache restore resolve. Hidden the moment content lands.
              Must stay BELOW the z-30 sticky overlays (status chip, AI-chat
              pill) — it only needs to cover the z-auto paper content. */}
          {initializing && (
            <div className="absolute inset-0 z-10 bg-white overflow-hidden">
              <DocumentSkeleton />
            </div>
          )}

          {/* Prototype CSS for .doc-letterhead / .doc-company-name / .doc-doc-title
              / .doc-resolved / .doc-meeting-table / .doc-sign-block etc. — without
              this the preview HTML renders as unstyled left-aligned text. */}
          <style>{DOC_CSS_SCOPED}</style>
          <div
            ref={contentRef}
            contentEditable={editable && canEdit}
            suppressContentEditableWarning
            onInput={() => { editedRef.current = true; scheduleAutoSave(); }}
            className="doc-preview"
            style={{ ...docEditorStyle({ editable, canEdit }), backgroundColor: '#fff' }}
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        </div>

        {/* Floating action — pinned to the MODAL's bottom-left corner while
            scrolling (not the centered paper). Self-labelled, so no tooltip. */}
        <div className="sticky z-30" style={{ bottom: 18, height: 0, overflow: 'visible' }}>
          <div className="pl-1">
            <button
              type="button"
              onClick={handleContinueInChat}
              aria-label="Open this document in the AI chat"
              className="inline-flex items-center gap-2 -translate-y-full pl-1.5 pr-3.5 h-9 rounded-full border bg-white text-[12px] font-semibold text-[#145f5f] transition-all duration-200 hover:shadow-xl hover:border-[#145f5f] active:scale-95"
              style={{ borderColor: 'rgba(20,95,95,0.3)', boxShadow: '0 10px 26px -10px rgba(8,17,31,0.35)' }}
            >
              <img src="/vee-mascot.svg" alt="" className="w-6 h-6 rounded-[7px]" />
              Open in AI Chat
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 py-[6px] flex items-center gap-2 border-t" style={{ borderColor: '#ddd6c6', background: '#fff' }}>
        <span
          className="text-[9.5px] px-[7px] py-[2px] rounded-full font-bold shrink-0 inline-flex items-center gap-1"
          style={
            docSource === 'ai'
              ? { background: '#e4f5f3', color: '#145f5f' }
              : docSource === 'fallback'
                ? { background: '#fff7e6', color: '#b8973a' }
                : { background: '#f0ebe0', color: '#7a7060' }
          }
        >
          <Icon name={docSource === 'ai' ? 'ai' : docSource === 'fallback' ? 'warning' : 'review'} size={10} />
          {docSource === 'ai' ? 'AI-generated' : docSource === 'fallback' ? 'Fallback' : 'Preview'}
        </span>
        {initialDraft?.refNumber && (
          <span
            className="text-[9.5px] px-[7px] py-[2px] rounded-full font-semibold shrink-0"
            style={{ background: '#f5f0e8', color: '#7a7060', fontFamily: "'JetBrains Mono', monospace" }}
            title="Document reference number"
          >
            {initialDraft.refNumber}
          </span>
        )}
        <span className="text-[10px] text-[#a09880]">
          Replace all [bracketed placeholders] with company-specific facts before final filing.
        </span>
      </div>

      {canShare && (
        <ShareDraftModal
          open={shareOpen}
          draft={{
            _id: initialDraft._id,
            documentTitle: initialDraft.documentTitle || doc?.title,
            companyName: initialDraft.companyName || companyName,
            documentType: initialDraft.documentType,
          }}
          onClose={() => setShareOpen(false)}
        />
      )}

      <ConfirmDialog
        open={confirmRevert}
        onClose={() => setConfirmRevert(false)}
        title="Revert to standard preview?"
        message={<>The document on screen resets to the default template preview.</>}
        note="Nothing is deleted — your generated/edited version stays saved and can be re-opened anytime from My Documents."
        confirmLabel="Revert"
        cancelLabel="Keep current"
        onConfirm={handleRevert}
      />
    </Modal>
  );
}
